import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import Cookies from 'js-cookie';

import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { setCoBorrowerChangedInfoAfterProvePreFill } from 'actions/application';
import { setCoBorrower } from 'actions/coBorrower';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import NumberInput from 'components/Inputs/NumberInput';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import Loader from 'components/Loader';
import { SearchLocationInput } from 'components/SearchLocationInput';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { MASKED_SSN_LENGTH, BORROWER_FIRST_LAST_NAME_MAX_LENGTH, SSN_MAX_LENGTH } from 'utils/constants';
import {
  getErrorMessageForRequiredFields,
  getStreetAddress,
  maskSSN,
  removeNonDigits,
  removeSpaces,
} from 'utils/stringUtils';
import { getMessageForInvalidFields, isValidEmail, validatePhoneNumber } from 'utils/validationUtils';
import { checkBirthDate } from 'utils/dateUtils';
import {
  validateFirstName,
  validateLastName,
} from 'pages/PreQualification/steps/BorrowerInformation/borrowerInformation.utils';
import { EyeIcon } from 'components/Icons';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { BorrowerDto } from 'api/BorrowerApi';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';
import { preQualificationApi } from 'api/PreQualification/PreQualificationApi';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'routes/routes.utils';
import { HFS_PRIVACY_POLICY_URL } from 'constants/constants';

import styles from './CoBorrowerInformation.module.scss';
import { EMAIL_CONFIRMATION } from '../LoanInformation/loanInformation.constants';
import { setPrequalificationPortal } from '../../../../actions/prequalificationPortal';

const CoBorrowerInformation = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);

  const {
    application,
    borrower: {
      borrowerId,
      streetName,
      streetNumber,
      city,
      zip,
      state,
      personalIdNumber: borrowerSSN,
      county,
      email: borrowerEmail,
      phone: borrowerPhone,
    },
    coBorrower: { firstName, lastName, personalIdNumber, phone, dateOfBirth, email, confirmEmail },
  } = useAppSelector((store) => store);

  const { register, setValue, trigger, watch, control, formState, getValues, clearErrors } = useForm({
    defaultValues: {
      [LoanInformationVariables.CoBorrowerFirstName]: firstName,
      [LoanInformationVariables.CoBorrowerLastName]: lastName,
      [LoanInformationVariables.CoBorrowerSSN]: personalIdNumber,
      [LoanInformationVariables.HomeStreetAddress]: getStreetAddress(streetNumber, streetName),
      [LoanInformationVariables.City]: city,
      [LoanInformationVariables.State]: state,
      [LoanInformationVariables.ZipCode]: zip,
      [LoanInformationVariables.County]: county || '',
      [LoanInformationVariables.CoBorrowerCellPhone]: phone,
      [LoanInformationVariables.CoBorrowerDateOfBirth]: dateOfBirth,
      [LoanInformationVariables.CoBorrowerEmail]: email,
      [LoanInformationVariables.ConfirmCoBorrowerEmail]: confirmEmail,
      [LoanInformationVariables.FullConsentCheckbox]: false,
    },
  });
  const watcher = watch();
  const [isSsnVisible, setSsnVisible] = useState(!application![LoanInformationVariables.IsCoBorrowerManualFlow]);

  const handleInputChange = () => {
    dispatch(setCoBorrowerChangedInfoAfterProvePreFill(true));
  };

  const checkMatchWithBorrowerSSN = (value: string) => {
    return value !== borrowerSSN || 'Must be different from Borrower SSN';
  };

  const checkMatchWithBorrowerEmail = (value: string) => {
    return value?.toLowerCase() !== borrowerEmail || 'Must be different from Borrower Email';
  };

  useEffect(() => {
    register(LoanInformationVariables.CoBorrowerFirstName, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerFirstName),
      validate: (value) => {
        return validateFirstName(value, LoanInformationVariables.CoBorrowerFirstName);
      },
    });
    register(LoanInformationVariables.CoBorrowerLastName, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerLastName),
      validate: (value) => {
        return validateLastName(value, LoanInformationVariables.CoBorrowerLastName);
      },
    });
    register(LoanInformationVariables.CoBorrowerSSN, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerSSN),
      validate: (value) =>
        value.length === SSN_MAX_LENGTH
          ? checkMatchWithBorrowerSSN(value)
          : getMessageForInvalidFields(LoanInformationVariables.SSN),
    });
    register(LoanInformationVariables.CoBorrowerCellPhone, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerCellPhone),
      validate: (value) => {
        if (borrowerPhone === value) {
          return 'Must be different from Borrower Cell Phone';
        }

        return validatePhoneNumber(value);
      },
    });
    register(LoanInformationVariables.CoBorrowerDateOfBirth, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerDateOfBirth),
      validate: (value) => checkBirthDate(value) || 'Please enter a valid date',
    });
    register(LoanInformationVariables.CoBorrowerEmail, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerEmail),
      validate: (value) =>
        isValidEmail(value) ? checkMatchWithBorrowerEmail(value) : 'Please enter a valid email address',
    });
    register(LoanInformationVariables.ConfirmCoBorrowerEmail, {
      required: getErrorMessageForRequiredFields(EMAIL_CONFIRMATION),
      validate: (value) =>
        !watcher[LoanInformationVariables.CoBorrowerEmail] ||
        !watcher[LoanInformationVariables.ConfirmCoBorrowerEmail] ||
        value?.toLowerCase() === watcher[LoanInformationVariables.CoBorrowerEmail] ||
        'Entered Email does not match Co-borrower Email',
    });
    register(LoanInformationVariables.FullConsentCheckbox, {
      required: false,
    });
  }, [register, watcher]);

  useEffect(() => {
    if (!!application![LoanInformationVariables.IsCoBorrowerManualFlow]) {
      dispatch(
        setCoBorrower({
          eligible: false,
        }),
      );
    }

    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.CoBorrowerInformation,
          prequalificationVersion: PrequalificationVersion.First,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const goToNextStep = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerIncome));
  };

  const handleSubmit = async () => {
    const result = await trigger();

    if (result) {
      dispatch(
        setPrequalificationPortal({
          confirmCoborrowerEmail: watcher[LoanInformationVariables.ConfirmCoBorrowerEmail],
        }),
      );
      dispatch(
        setCoBorrower({
          firstName: watcher[LoanInformationVariables.CoBorrowerFirstName].trim(),
          lastName: watcher[LoanInformationVariables.CoBorrowerLastName].trim(),
          personalIdNumber: watcher[LoanInformationVariables.CoBorrowerSSN],
          dateOfBirth: watcher[LoanInformationVariables.CoBorrowerDateOfBirth],
          email: watcher[LoanInformationVariables.CoBorrowerEmail],
          phone: watcher[LoanInformationVariables.CoBorrowerCellPhone],
          fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
          consentAgreed: true,
        }),
      );

      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      try {
        const coBorrower = await preQualificationApi.getCoborrowerShortInfoBySsn({
          ssn: watcher[LoanInformationVariables.CoBorrowerSSN],
        });

        if (coBorrower.id) {
          const { hasBorrowerApplications, hasCoborrowerApplications } = coBorrower;

          if (hasBorrowerApplications || hasCoborrowerApplications || coBorrower.locked) {
            dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerAlreadyHaveActiveApplication));

            return;
          } else {
            dispatch(setCoBorrower({ borrowerId: coBorrower.id }));
            goToNextStep();
          }
        } else {
          const newCoBorrower = await preQualificationApi.createBorrower({
            firstName: watcher[LoanInformationVariables.CoBorrowerFirstName],
            lastName: watcher[LoanInformationVariables.CoBorrowerLastName],
            personalIdNumber: watcher[LoanInformationVariables.CoBorrowerSSN],
            phone: watcher[LoanInformationVariables.CoBorrowerCellPhone],
            email: watcher[LoanInformationVariables.CoBorrowerEmail],
            address: {
              streetName: streetName,
              streetNumber: streetNumber!,
              city: city,
              stateOrProvince: state,
              zipOrPostalCode: zip,
              county: county,
            },
            dateOfBirth: watcher[LoanInformationVariables.CoBorrowerDateOfBirth],
            fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
          });

          if ((newCoBorrower as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
            notificationManager.notifyFailure(FailureNotificationType.CoBorrowerEmailAlreadyExists);

            return;
          }

          const borrower = newCoBorrower as BorrowerDto;

          dispatch(setCoBorrower({ borrowerId: borrower.id as string }));
          goToNextStep();
        }

        await preQualificationApi.updateBorrowerMarketingConsent({
          email: watcher[LoanInformationVariables.CoBorrowerEmail],
          fullConsentAgreed: !!watcher[LoanInformationVariables.FullConsentCheckbox],
          consentAgreed: true,
        });
      } catch (error: any) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  };

  const handleBack = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
  };
  const handleCopyPaste = (event: React.ClipboardEvent) => event.preventDefault();

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <FormHeader title="Co-Borrower Information" percent={66} />
          <FormBody>
            <div className={styles.rowContainer}>
              <Input
                label={LoanInformationVariables.CoBorrowerFirstName}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerFirstName]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerFirstName}
                onChange={(event) => {
                  handleInputChange();
                  setValue(LoanInformationVariables.CoBorrowerFirstName, event.target.value);
                  trigger(LoanInformationVariables.CoBorrowerFirstName);
                }}
                value={watcher[LoanInformationVariables.CoBorrowerFirstName]}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
              />
              <Input
                label={LoanInformationVariables.CoBorrowerLastName}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerLastName]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerLastName}
                onChange={(event) => {
                  handleInputChange();
                  setValue(LoanInformationVariables.CoBorrowerLastName, event.target.value);
                  trigger(LoanInformationVariables.CoBorrowerLastName);
                }}
                value={watcher[LoanInformationVariables.CoBorrowerLastName]}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
              />
              <Input
                label={LoanInformationVariables.CoBorrowerSSN}
                className={clsx(styles.formInput, styles.ssnInput)}
                name={LoanInformationVariables.CoBorrowerSSN}
                type={isSsnVisible ? 'text' : 'password'}
                onChange={(event) => {
                  handleInputChange();

                  const value = removeNonDigits(event.target.value.trim());

                  setValue(LoanInformationVariables.CoBorrowerSSN, value);

                  clearErrors(LoanInformationVariables.CoBorrowerSSN);

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerSSN);
                  }
                }}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerSSN]?.message}
                value={
                  application![LoanInformationVariables.IsCoBorrowerManualFlow]
                    ? maskSSN(watcher[LoanInformationVariables.CoBorrowerSSN], !isSsnVisible)
                    : `***-**-${watcher[LoanInformationVariables.CoBorrowerSSN].substr(5)}`
                }
                disabled={!application![LoanInformationVariables.IsCoBorrowerManualFlow]}
                maxLength={isSsnVisible ? MASKED_SSN_LENGTH : SSN_MAX_LENGTH}
                inputMode="numeric"
                endAdornment={() =>
                  application![LoanInformationVariables.IsCoBorrowerManualFlow] ? (
                    <EyeIcon
                      open={isSsnVisible}
                      className={styles.eyeIcon}
                      onClick={() => {
                        setSsnVisible(!isSsnVisible);
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            </div>
            <SearchLocationInput
              control={control}
              containerClassName={styles.addressContainer}
              disabled
              useFormControl
              streetLabel={LoanInformationVariables.HomeStreetAddress}
              initialValue={{
                streetAddress: watcher[LoanInformationVariables.HomeStreetAddress],
                city: watcher[LoanInformationVariables.City],
                state: watcher[LoanInformationVariables.State],
                zip: watcher[LoanInformationVariables.ZipCode],
              }}
              getValues={getValues}
            />
            <div className={styles.rowContainer}>
              <PhoneNumberInput
                label={LoanInformationVariables.CoBorrowerCellPhone}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerCellPhone]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerCellPhone}
                onChange={(value: string) => {
                  clearErrors([LoanInformationVariables.CoBorrowerCellPhone]);
                  setValue(LoanInformationVariables.CoBorrowerCellPhone, value);

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerCellPhone);
                  }
                }}
                value={watcher[LoanInformationVariables.CoBorrowerCellPhone]}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.CoBorrowerCellPhone]) {
                    trigger(LoanInformationVariables.CoBorrowerCellPhone);
                  }
                }}
              />
              <NumberInput
                label={LoanInformationVariables.CoBorrowerDateOfBirth}
                placeholder="MM/DD/YYYY"
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerDateOfBirth]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerDateOfBirth}
                onChange={(event) => {
                  clearErrors(LoanInformationVariables.CoBorrowerDateOfBirth);
                  setValue(LoanInformationVariables.CoBorrowerDateOfBirth, event.target.value);
                }}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.CoBorrowerDateOfBirth]) {
                    trigger(LoanInformationVariables.CoBorrowerDateOfBirth);
                  }
                }}
                format="##/##/####"
                value={watcher[LoanInformationVariables.CoBorrowerDateOfBirth]}
                mask="_"
                allowEmptyFormatting
              />
            </div>
            <div className={styles.rowContainer}>
              <Input
                label={LoanInformationVariables.CoBorrowerEmail}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerEmail]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerEmail}
                onChange={(event: any) => {
                  clearErrors([
                    LoanInformationVariables.CoBorrowerEmail,
                    LoanInformationVariables.ConfirmCoBorrowerEmail,
                  ]);
                  setValue(LoanInformationVariables.CoBorrowerEmail, removeSpaces(event.target.value?.toLowerCase()));

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerEmail);
                  }
                }}
                onBlur={() => {
                  if (watcher[LoanInformationVariables.ConfirmCoBorrowerEmail]) {
                    trigger(LoanInformationVariables.ConfirmCoBorrowerEmail);
                  }
                }}
                value={watcher[LoanInformationVariables.CoBorrowerEmail]}
                onCopy={handleCopyPaste}
              />
              <Input
                label={LoanInformationVariables.ConfirmCoBorrowerEmail}
                errorMessage={formState.errors[LoanInformationVariables.ConfirmCoBorrowerEmail]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.ConfirmCoBorrowerEmail}
                onChange={(event: any) => {
                  clearErrors([
                    LoanInformationVariables.CoBorrowerEmail,
                    LoanInformationVariables.ConfirmCoBorrowerEmail,
                  ]);
                  setValue(
                    LoanInformationVariables.ConfirmCoBorrowerEmail,
                    removeSpaces(event.target.value?.toLowerCase()),
                  );

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.ConfirmCoBorrowerEmail);
                  }
                }}
                onBlur={() => {
                  clearErrors(LoanInformationVariables.ConfirmCoBorrowerEmail);

                  if (
                    formState.isSubmitted ||
                    (watcher[LoanInformationVariables.CoBorrowerEmail] &&
                      watcher[LoanInformationVariables.ConfirmCoBorrowerEmail])
                  ) {
                    trigger(LoanInformationVariables.ConfirmCoBorrowerEmail);
                  }
                }}
                onPaste={handleCopyPaste}
                value={watcher[LoanInformationVariables.ConfirmCoBorrowerEmail]}
                autoComplete="new-password"
              />
            </div>
            <div className={styles.consentText}>
              I agree to receive text messages from HFS Financial, LLC at the phone number provided. These messages may
              include transactional communications, status updates, and appointment reminders. Message frequency may
              vary. Standard message and data rates may apply. Reply STOP at any time to unsubscribe. For help, reply
              HELP or contact us at 800-254-9560. Please review our{' '}
              <a className={styles.link} href={HFS_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              to learn how your data is used.
            </div>
            <Controller
              name={LoanInformationVariables.FullConsentCheckbox}
              control={control}
              rules={{
                required: false,
              }}
              render={() => (
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    className={styles.checkboxText}
                    type={CheckboxType.Alternative}
                    name="agreementCheckbox"
                    isChecked={!!watcher[LoanInformationVariables.FullConsentCheckbox]}
                    onChange={() => {
                      setValue(
                        LoanInformationVariables.FullConsentCheckbox,
                        !watcher[LoanInformationVariables.FullConsentCheckbox],
                      );
                    }}
                    text={`Co-Borrower expressly consents to receiving calls and messages, including auto-dialed and pre-recorded
                     message calls, and SMS messages (including text messages) from HFS Financial at any telephone numbers
                      that you have provided or may provide in the future (including any cellular telephone numbers).  
                      HFS Financial may send text messages in various formats and with various contents, including 
                      but not limited to, text messages about products, services, and transactions you have requested/initiated; 
                      and/or marketing the products and services offered by or through us. You are the owner or authorized
                       user of the mobile phone number you have provided and you will notify us immediately 
                       if you are no longer the owner or authorized user of the mobile phone number you have provided.
                        Your cellular or mobile telephone provider will charge you according to the type of plan you carry.`}
                    labelClassName={styles.checkboxTextContainer}
                  />
                </div>
              )}
            />
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleSubmit} withArrow>
              Next
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default CoBorrowerInformation;
